import { z } from 'zod'
import { httpUrlSchema } from '../helpers/asset-ref.js'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { adPlatformSchema } from '../enums/ad-platform.enum.js'
import { adAccountUseSchema } from '../enums/ad-account-use.enum.js'

/**
 * Ad accounts and the sign-ins that reach them, as the company's Ads page sees
 * them (docs/ad-analytics.spec.md). **No shape here carries a credential**: a
 * sign-in goes in once, sealed, and only who signed in ever comes back.
 */

/** One sign-in, owned by the company. */
export const adConnectionDtoSchema = z.object({
  id: z.string().uuid(),
  platform: adPlatformSchema,
  /** Who signed in, as the platform reported it. */
  email: z.string(),
  /** Who in the company made the connection; null once they are gone. */
  connectedBy: z.string().nullable(),
  connectedAt: instantSchema,
  /** When the platform started refusing the sign-in; null while it works. */
  failingSince: instantSchema.nullable(),
  lastCheckedAt: instantSchema.nullable(),
  /** How many of the company's accounts it reaches. */
  accountCount: z.number().int().nonnegative(),
})
export type AdConnectionDto = z.infer<typeof adConnectionDtoSchema>

export const adAccountDtoSchema = z.object({
  id: z.string().uuid(),
  platform: adPlatformSchema,
  /** The platform's own id. For Google Ads, digits only (see `formatGoogleAdsId`). */
  externalId: z.string(),
  name: z.string(),
  currencyCode: z.string(),
  timeZone: z.string(),
  use: adAccountUseSchema,
  /** Where its campaigns count unless they have a place of their own. Set when in use or stopped. */
  defaultVenueId: z.string().uuid().nullable(),
  defaultTrackId: z.string().uuid().nullable(),
  /** The default venue has been deleted: the account stops updating until it is pointed elsewhere. */
  defaultVenueDeleted: z.boolean(),
  /** Google says the account is closed (canceled, suspended or closed). */
  closedSince: instantSchema.nullable(),
  /** In use, and no working connection reaches it. */
  unreachableSince: instantSchema.nullable(),
  /** The connections that reach it. */
  connectionIds: z.array(z.string().uuid()),
  historyFrom: calendarDaySchema.nullable(),
  /** The last of its own days whose numbers are in. */
  importedThrough: calendarDaySchema.nullable(),
  /** Its first import has finished; until then none of its numbers show. */
  historyComplete: z.boolean(),
  /** An import is running for it right now. */
  importing: z.boolean(),
  lastUpdatedAt: instantSchema.nullable(),
})
export type AdAccountDto = z.infer<typeof adAccountDtoSchema>

/** A campaign in an account in use or stopped, and where it counts. */
export const adCampaignDtoSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  name: z.string(),
  /** A place of its own; null follows the account. */
  ownVenueId: z.string().uuid().nullable(),
  ownTrackId: z.string().uuid().nullable(),
  /** Its own venue has been deleted: it needs pointing elsewhere. */
  ownVenueDeleted: z.boolean(),
})
export type AdCampaignDto = z.infer<typeof adCampaignDtoSchema>

/** A venue an account or campaign can point at, with its tracks. Only live venues. */
export const adVenueOptionDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  tracks: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
})
export type AdVenueOptionDto = z.infer<typeof adVenueOptionDtoSchema>

/** Everything the company's Ads page sets up from. */
export const adsSetupDtoSchema = z.object({
  /**
   * Whether Google Ads can be connected in this environment at all. False when
   * FLOH Venues' own Google set-up is missing; the page says so plainly.
   */
  googleAvailable: z.boolean(),
  connections: z.array(adConnectionDtoSchema),
  accounts: z.array(adAccountDtoSchema),
  campaigns: z.array(adCampaignDtoSchema),
  venues: z.array(adVenueOptionDtoSchema),
})
export type AdsSetupDto = z.infer<typeof adsSetupDtoSchema>

/**
 * What removing an account from its venue would delete: the spend each venue
 * would lose, so the person confirming can see what goes.
 */
export const adRemovalPreviewDtoSchema = z.object({
  accountId: z.string().uuid(),
  venues: z.array(
    z.object({
      venueId: z.string().uuid(),
      venueName: z.string(),
      currencyCode: z.string(),
      spendMicros: z.number().int().nonnegative(),
    }),
  ),
  /** How many campaigns, and their numbers, go with it. */
  campaignCount: z.number().int().nonnegative(),
})
export type AdRemovalPreviewDto = z.infer<typeof adRemovalPreviewDtoSchema>

/** A refresh that was started: how many accounts are being read again. */
export const adRefreshDtoSchema = z.object({ accounts: z.number().int().nonnegative() })
export type AdRefreshDto = z.infer<typeof adRefreshDtoSchema>

/** Where to send someone to sign in with Google. http(s) only, as the billing redirect: the app assigns it to the window. */
export const adSignInStartDtoSchema = z.object({ url: httpUrlSchema })
export type AdSignInStartDto = z.infer<typeof adSignInStartDtoSchema>

/** A finished sign-in. */
export const adSignInDoneDtoSchema = z.object({
  connectionId: z.string().uuid(),
  /** The company already had this person's sign-in; it was renewed rather than added. */
  renewed: z.boolean(),
  /** Accounts this sign-in found that the company had never seen. */
  newAccountIds: z.array(z.string().uuid()),
  /**
   * The sign-in was kept, but its accounts couldn't be listed just now: Google
   * didn't answer, or can't be read yet. They appear by the next day without
   * anyone signing in again.
   */
  accountsPending: z.boolean(),
  /** The venue the sign-in was started from, if any, to offer the new accounts to. */
  venueId: z.string().uuid().nullable(),
})
export type AdSignInDoneDto = z.infer<typeof adSignInDoneDtoSchema>
